import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Star, 
  MapPin, 
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Quote,
  Clock,
  Calendar,
  Users,
  Globe,
  Sparkles,
  Volume2,
  DollarSign,
  Car,
  Download,
  Award,
  Leaf,
  Home,
  Instagram,
  Facebook
} from 'lucide-react';

// Constants - Reusing existing booking links
const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';
const BOOKING_URL = 'https://book.abbamassage.com/?aff=29';
const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/G2JL5nKRZdTESBVXA';
const TRIPADVISOR_URL = 'https://www.tripadvisor.com.mx/Attraction_Review-g150807-d34151326-Reviews-Abba_Massage_Studio_and_Wellness_Center-Cancun_Yucatan_Peninsula.html';
const INSTAGRAM_URL = 'https://www.instagram.com/abbamassage';
const FACEBOOK_URL = 'https://www.facebook.com/abbamassage';

const STUDIO_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": [
        "DaySpa",
        "HealthAndBeautyBusiness"
      ],
      "@id": "https://abbamassage.com/#business",
      "name": "ABBA Massage Studio & Wellness Center",
      "url": "https://abbamassage.com/",
      "mainEntityOfPage": "https://abbamassage.com/studio-services",
      "description": "Private massage studio in Cancún offering personalized 60 and 90-minute massage sessions by appointment.",
      "image": "https://customer-assets.emergentagent.com/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/ezxbu1pd_CLIENT%20SIPING%20TEA.png",
      "telephone": "+52 998 303 2313",
      "email": "hello@abbamassage.com",
      "priceRange": "MXN $960-$1,320",
      "currenciesAccepted": [
        "MXN",
        "USD"
      ],
      "paymentAccepted": [
        "Cash",
        "Credit Card",
        "Debit Card"
      ],
      "hasMap": "https://maps.app.goo.gl/G2JL5nKRZdTESBVXA",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Calle Playa Brava 26, Manzana 1, Lote 53",
        "addressLocality": "Cancún",
        "addressRegion": "Quintana Roo",
        "postalCode": "77508",
        "addressCountry": "MX"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "09:00",
        "closes": "20:00"
      },
      "areaServed": {
        "@type": "City",
        "name": "Cancún"
      },
      "knowsLanguage": [
        "English",
        "Spanish"
      ],
      "sameAs": [
        "https://www.instagram.com/abbamassage",
        "https://www.facebook.com/abbamassage",
        "https://www.youtube.com/@abbamassage",
        "https://maps.app.goo.gl/G2JL5nKRZdTESBVXA",
        "https://www.tripadvisor.com.mx/Attraction_Review-g150807-d34151326-Reviews-Abba_Massage_Studio_and_Wellness_Center-Cancun_Yucatan_Peninsula.html"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Massage services at ABBA Massage Studio",
        "itemListElement": [
          {
            "@id": "https://abbamassage.com/studio-services#offer-60"
          },
          {
            "@id": "https://abbamassage.com/studio-services#offer-90"
          }
        ]
      }
    },
    {
      "@type": "Service",
      "@id": "https://abbamassage.com/studio-services#massage-60",
      "name": "60-Minute Massage in Cancún",
      "alternateName": "Masaje de 60 minutos en Cancún",
      "description": "Personalized 60-minute massage at ABBA Massage Studio in Cancún.",
      "serviceType": "Massage therapy",
      "provider": {
        "@id": "https://abbamassage.com/#business"
      },
      "areaServed": {
        "@type": "City",
        "name": "Cancún"
      },
      "offers": {
        "@id": "https://abbamassage.com/studio-services#offer-60"
      }
    },
    {
      "@type": "Offer",
      "@id": "https://abbamassage.com/studio-services#offer-60",
      "url": "https://book.abbamassage.com/?aff=29",
      "price": "960",
      "priceCurrency": "MXN",
      "availability": "https://schema.org/InStock",
      "itemOffered": {
        "@id": "https://abbamassage.com/studio-services#massage-60"
      }
    },
    {
      "@type": "Service",
      "@id": "https://abbamassage.com/studio-services#massage-90",
      "name": "90-Minute Massage in Cancún",
      "alternateName": "Masaje de 90 minutos en Cancún",
      "description": "Personalized 90-minute massage at ABBA Massage Studio in Cancún.",
      "serviceType": "Massage therapy",
      "provider": {
        "@id": "https://abbamassage.com/#business"
      },
      "areaServed": {
        "@type": "City",
        "name": "Cancún"
      },
      "offers": {
        "@id": "https://abbamassage.com/studio-services#offer-90"
      }
    },
    {
      "@type": "Offer",
      "@id": "https://abbamassage.com/studio-services#offer-90",
      "url": "https://book.abbamassage.com/?aff=29",
      "price": "1320",
      "priceCurrency": "MXN",
      "availability": "https://schema.org/InStock",
      "itemOffered": {
        "@id": "https://abbamassage.com/studio-services#massage-90"
      }
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://abbamassage.com/studio-services#breadcrumb",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ABBA Massage",
          "item": "https://abbamassage.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Massage Studio in Cancun",
          "item": "https://abbamassage.com/studio-services"
        }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": "https://abbamassage.com/studio-services#faq",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do I need to book an appointment?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Our studio is open daily by appointment. Because our therapists also provide in-home services throughout Cancún, advance booking ensures that a therapist and treatment room are ready exclusively for you."
          }
        },
        {
          "@type": "Question",
          "name": "What are the studio hours?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ABBA Massage is available every day from 9:00 AM to 8:00 PM, by appointment only."
          }
        },
        {
          "@type": "Question",
          "name": "How much is a massage at the studio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A 60-minute massage costs MXN $960 and a 90-minute massage costs MXN $1,320. Guests may choose the technique they need without additional fees."
          }
        },
        {
          "@type": "Question",
          "name": "Where is ABBA Massage located?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ABBA Massage is located at Calle Playa Brava 26, Manzana 1, Lote 53, 77508 Cancún, Quintana Roo."
          }
        },
        {
          "@type": "Question",
          "name": "What is included in the experience?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Each personalized session includes natural products, sound healing and tea after the treatment."
          }
        },
      {
  "@type": "Question",
  "name": "Do you offer couples massage in Cancun?",
  "acceptedAnswer": {
    "@type": "Answer",
    "text": "Yes. Our Cancun studio has a private double treatment room for couples massage. Each guest can choose the massage technique and pressure that best suits their needs."
  }
},
{
  "@type": "Question",
  "name": "What type of massage should I choose?",
  "acceptedAnswer": {
    "@type": "Answer",
    "text": "You do not need to decide before your appointment. Our certified therapists can personalize your session using relaxation, therapeutic, deep tissue, sports or muscle recovery, lymphatic, Shiatsu and cupping techniques. Thai Massage and Hot Stones require a 90-minute booking; prenatal massage is available as a 60-minute session. Please request Thai Massage, cupping, Hot Stones or a male therapist when booking so we can confirm availability."
  }
},
{
  "@type": "Question",
  "name": "Is ABBA Massage near the Cancun Hotel Zone?",
  "acceptedAnswer": {
    "@type": "Answer",
    "text": "Yes. ABBA Massage is located in downtown Cancun and is easily accessible from the Hotel Zone. In the app, search for \"Abba Massage Studio & Wellness Center\"."
  }
},
{
  "@type": "Question",
  "name": "Can I book a same-day massage in Cancun?",
  "acceptedAnswer": {
    "@type": "Answer",
    "text": "Yes. Same-day massage appointments are available when our schedule allows. Contact us on WhatsApp to check the latest availability."
  }
},
{
  "@type": "Question",
  "name": "Why choose ABBA Massage instead of a hotel or resort spa in Cancun?",
  "acceptedAnswer": {
    "@type": "Answer",
    "text": "ABBA Massage offers a personalized, unrushed massage experience with experienced therapists and transparent pricing: MXN $960 for 60 minutes and MXN $1,320 for 90 minutes. Even after paying for an Uber from the Hotel Zone, many guests find they receive an exceptional experience for a fraction of typical resort-spa prices. One recent Google reviewer described her massage with us as \"elite-tier\" and quite possibly one of the best massages she had ever received."
  }
}]
    }
  ]
};

// Price List - Same as other pages
const PRICE_LIST = {
  en: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/uy0hf9bf_prices-abba-2026-en.png',
  es: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/icmd5yzn_precios-abba-2026-es.png'
};

// ONLY the three provided images
const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/jo19zhtt_Hero%20Studio%20Services.png',
  studio: 'https://customer-assets.emergentagent.com/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/ezxbu1pd_CLIENT%20SIPING%20TEA.png',
  therapists: 'https://customer-assets.emergentagent.com/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/7yvrbb88_TERAPEUTAS%20ABBA.JPG',
};

// Exact content as specified - NO invented content
const content = {
  es: {
    hero: {
      badge: 'Estudio de Masaje Mejor Calificado en Cancún',
      title: 'Estudio de Masajes en Cancún | ABBA Massage',
      summary: 'ABBA Massage es un estudio privado de masajes en Cancún, abierto todos los días de 9:00 a. m. a 8:00 p. m. con cita previa. Sesiones personalizadas desde $960 MXN, con terapeutas certificados y calificación perfecta en Google.',
      subtitle: 'Más que un masaje.',
      subtitleLine2: 'Un espacio para volver a ti.',
      cta: 'Reservar ahora',
      ctaSecondary: 'Ver precios',
    },
    whyReturn: {
      label: 'Experiencia',
      title: 'Por qué nuestros huéspedes regresan',
      items: [
        { icon: 'star', text: 'Calificación perfecta en Google' },
        { icon: 'award', text: 'Registro Nacional de Turismo y aviso de funcionamiento COFEPRIS' },
        { icon: 'award', text: 'Top Rated en TripAdvisor' },
        { icon: 'clock', text: '15+ años de experiencia en hospitalidad de lujo' },
        { icon: 'leaf', text: 'Manteca de karité natural' },
        { icon: 'sound', text: 'Sonoterapia incluida' },
        { icon: 'dollar', text: 'Precios transparentes' },
        { icon: 'car', text: 'Fácil acceso en Uber' },
        { icon: 'globe', text: 'Atención en inglés y español' },
        { icon: 'home', text: 'Estudio privado y silencioso' },
      ],
    },
    pricing: {
      label: 'Precios',
      title: 'Precios Transparentes',
      subtitle: 'Tarifas fijas. Sin sorpresas. ',
      items: [
        { duration: '60 minutos', mxn: '$960', usd: '$60' },
        { duration: '90 minutos', mxn: '$1,320', usd: '$85' },
      ],
      techniquesTitle: 'Técnicas disponibles',
      techniques: ['Relajante', 'Terapéutico', 'Deep Tissue', 'Deportivo / descarga muscular', 'Linfático', 'Shiatsu', 'Cupping'],
      techniqueNote: 'Thai y Hot Stones están disponibles en sesiones de 90 minutos. El masaje prenatal está disponible en sesiones de 60 minutos. Solicita Thai, cupping, Hot Stones o terapeuta hombre al reservar para confirmar disponibilidad.',
      cta: 'Ver Precios',
    },
    studio: {
      label: 'Estudio',
      title: 'Un estudio privado y silencioso, diseñado para que vuelvas a ti',
      description: 'Nuestro estudio fue diseñado para bajar el ritmo desde el momento en que entras.',
      description2: 'Cada detalle fue pensado para ayudarte a desconectar: luz natural, productos naturales, y un momento de té para que la experiencia continúe incluso después del masaje.',
      bullets: [
        'Sound Healing incluido',
        'Productos naturales conscientes',
        'Té después del tratamiento',
        'Estudio privado y silencioso',
      ],
    },
    therapists: {
      label: 'Nuestro Equipo',
      title: 'Conoce a nuestros terapeutas',
      description: 'Siete profesionales certificados con un promedio de más de 10 años de experiencia. Cada sesión se adapta a tus necesidades — nunca un masaje igual para todos.',
      stats: [
        { value: '10+', label: 'Años promedio' },
        { value: '7', label: 'Terapeutas' },
        { value: 'EN/ES', label: 'Idiomas' },
      ],
    },
    testimonials: {
      label: 'Testimonios',
      title: 'Lo que dicen nuestros huéspedes',
      subtitle: 'Reseñas reales de Google y TripAdvisor',
      items: [
        {
          text: 'El estudio está impecablemente limpio. Se siente el nivel de un resort desde el momento en que entras.',
          source: 'TripAdvisor',
        },
        {
          text: 'Una experiencia holística de principio a fin. Incluso el té al final fue un detalle hermoso.',
          source: 'Google',
        },
        {
          text: 'La sonoterapia al final fue inesperada y hermosa. Una experiencia realmente de lujo.',
          source: 'Google',
        },
        {
          text: 'Sin duda el mejor masaje que he recibido. Vale completamente el viaje en Uber desde la Zona Hotelera.',
          source: 'Google',
        },
      ],
    },
    faq: {
      label: 'Preguntas Frecuentes',
      title: 'Antes de visitar nuestro estudio',
 items: [
  {
    question: '¿Necesito reservar una cita?',
    answer: 'Sí. Abrimos todos los días únicamente con cita previa. Como nuestros terapeutas también realizan servicios a domicilio en Cancún, reservar con anticipación asegura que haya un terapeuta y una cabina preparados exclusivamente para ti.',
  },
  {
    question: '¿Cuál es el horario del estudio?',
    answer: 'Estamos disponibles todos los días de 9:00 a. m. a 8:00 p. m., siempre con cita previa.',
  },
  {
    question: '¿Cuánto cuesta un masaje en el estudio?',
    answer: 'La sesión de 60 minutos cuesta MXN $960 y la de 90 minutos MXN $1,320. Puedes elegir la técnica que necesitas sin cargos adicionales.',
  },
  {
    question: '¿Dónde está ubicado ABBA Massage?',
    answer: 'Estamos en Calle Playa Brava 26, Manzana 1, Lote 53, CP 77508, Cancún, Quintana Roo.',
  },
  {
    question: '¿Qué incluye la experiencia?',
    answer: 'Cada sesión es personalizada e incluye productos naturales, sonoterapia y té después del tratamiento.',
  },
  {
    question: '¿Ofrecen masaje para parejas en Cancún?',
    answer: 'Sí. Nuestro estudio en Cancún cuenta con una cabina doble privada para masaje en pareja. Cada persona puede elegir la técnica y presión que mejor se adapte a sus necesidades.',
  },
  {
    question: '¿Qué tipo de masaje debo elegir?',
    answer: 'No necesitas decidirlo antes de tu cita. Nuestros terapeutas certificados pueden personalizar tu sesión con técnicas de masaje relajante, terapéutico, Deep Tissue, deportivo o descarga muscular, linfático, Shiatsu y cupping. Thai y Hot Stones requieren una sesión de 90 minutos; prenatal está disponible en 60 minutos. Solicita Thai, cupping, Hot Stones o terapeuta hombre al reservar para confirmar disponibilidad.',
  },
  {
    question: '¿ABBA Massage está cerca de la Zona Hotelera de Cancún?',
    answer: 'Sí. ABBA Massage está ubicado en el centro de Cancún y es fácilmente accesible desde la Zona Hotelera. En la app, búscanos como "Abba Massage Studio & Wellness Center".',
  },
  {
    question: '¿Puedo reservar un masaje el mismo día en Cancún?',
    answer: 'Sí. Tenemos citas para el mismo día cuando la disponibilidad lo permite. Contáctanos por WhatsApp para consultar los horarios disponibles.',
  },
  {
    question: '¿Por qué elegir ABBA Massage en lugar del spa de un hotel o resort en Cancún?',
    answer: 'ABBA Massage ofrece una experiencia personalizada y sin prisas, con terapeutas experimentados y precios transparentes: MXN $960 por 60 minutos y MXN $1,320 por 90 minutos. Incluso pagando un Uber desde la Zona Hotelera, puedes ahorrar considerablemente frente a los precios habituales de los spas de resort. Una huésped nos describió recientemente en Google como una experiencia "elite-tier" y posiblemente uno de los mejores masajes que ha recibido.',
  },
],
    },
    founder: {
      label: 'Fundadora',
      title: 'Conoce a la Fundadora',
      name: 'Mariana, Fundadora.',
      description: 'Ex-Ejecutiva de Spa de Resorts de Lujo, con más de 25 años en hospitalidad y más de 15 años liderando operaciones de spa de lujo.',
      description2: 'ABBA Massage combina los estándares de un resort con un cuidado personalizado y genuino.',
      cta: 'Conoce a Mariana',
    },
  },
  en: {
    hero: {
      badge: 'Top Rated Massage Studio in Cancun',
      title: 'Massage Studio in Cancun | ABBA Massage',
      summary: 'ABBA Massage is a private massage studio in Cancun, open daily from 9:00 AM to 8:00 PM by appointment. Personalized sessions start at MXN $960, with certified therapists and a perfect Google rating.',
      subtitle: 'More than a massage.',
      subtitleLine2: 'A place to reconnect with yourself.',
      cta: 'Book Now',
      ctaSecondary: 'View Prices',
    },
    whyReturn: {
      label: 'Experience',
      title: 'Why Guests Return',
      items: [
        { icon: 'star', text: 'Perfect Google Rating' },
        { icon: 'award', text: 'National Tourism Registry and COFEPRIS operating notice' },
        { icon: 'award', text: 'Top Rated on TripAdvisor' },
        { icon: 'clock', text: '15+ years luxury hospitality experience' },
        { icon: 'leaf', text: 'Natural shea butter' },
        { icon: 'sound', text: 'Sound Healing included' },
        { icon: 'dollar', text: 'Transparent pricing' },
        { icon: 'car', text: 'Easy Uber access' },
        { icon: 'globe', text: 'English & Spanish' },
        { icon: 'home', text: 'Private peaceful studio' },
      ],
    },
    pricing: {
      label: 'Pricing',
      title: 'Transparent Pricing',
      subtitle: 'Fixed rates. No surprises.',
      items: [
        { duration: '60 Minutes', mxn: '$960', usd: '$60' },
        { duration: '90 Minutes', mxn: '$1,320', usd: '$85' },
      ],
      techniquesTitle: 'Available techniques',
      techniques: ['Relaxation', 'Therapeutic', 'Deep Tissue', 'Sports / Muscle Recovery', 'Lymphatic', 'Shiatsu', 'Cupping'],
      techniqueNote: 'Thai Massage and Hot Stones are available as 90-minute sessions. Prenatal massage is available as a 60-minute session. Request Thai, cupping, Hot Stones or a male therapist when booking so we can confirm availability.',
      cta: 'View Prices',
    },
    studio: {
      label: 'Studio',
      title: 'A quiet private studio designed to help you reconnect with yourself',
      description: 'Our studio was designed to slow you down from the moment you walk in.',
      description2: 'Every detail was chosen to help you disconnect: natural light, natural products, and a tea moment so the experience continues even after the massage.',
      bullets: [
        'Sound Healing included',
        'Natural conscious products',
        'Tea after treatment',
        'Private peaceful studio',
      ],
    },
    therapists: {
      label: 'Our Team',
      title: 'Meet Our Therapists',
      description: 'Seven certified massage professionals averaging more than ten years of experience. Every treatment is personalized — never the same massage for everyone.',
      stats: [
        { value: '10+', label: 'Average years' },
        { value: '7', label: 'Therapists' },
        { value: 'EN/ES', label: 'Languages' },
      ],
    },
    testimonials: {
      label: 'Testimonials',
      title: 'What Our Guests Say',
      subtitle: 'Real reviews from Google and TripAdvisor',
      items: [
        {
          text: 'The studio is impeccably clean, calm and safe. You can feel the resort-level standards from the moment you walk in.',
          source: 'TripAdvisor',
        },
        {
          text: 'Absolutely amazing massage. If you\'re staying at a resort, do not make the mistake of getting a subpar (and overpriced) massage at the resort. This was elite-tier, quite possibly one of the best massages I\'ve ever gotten.',
          source: 'Google',
        },
        {
          text: 'The sound healing at the end was unexpected and beautiful — a genuinely luxurious experience.',
          source: 'Google',
        },
        {
          text: 'Hands down the best massage I have ever had. Truly worth the Uber ride from Cancun\'s Hotel Zone.',
          source: 'Google',
        },
      ],
    },
    faq: {
      label: 'Frequently Asked Questions',
      title: 'Before visiting our studio',
      items: [
  {
    question: 'Do I need to book an appointment?',
    answer: 'Yes. Our studio is open daily by appointment. Because our therapists also provide in-home services throughout Cancun, advance booking ensures that a therapist and treatment room are ready exclusively for you.',
  },
  {
    question: 'What are the studio hours?',
    answer: 'We are available every day from 9:00 AM to 8:00 PM, by appointment only.',
  },
  {
    question: 'How much is a massage at the studio?',
    answer: 'A 60-minute session is MXN $960 and a 90-minute session is MXN $1,320. Choose the technique you need with no additional fees.',
  },
  {
    question: 'Where is ABBA Massage located?',
    answer: 'We are located at Calle Playa Brava 26, Manzana 1, Lote 53, 77508 Cancún, Quintana Roo.',
  },
  {
    question: 'What is included in the experience?',
    answer: 'Each personalized session includes natural products, sound healing and tea after the treatment.',
  },
  {
    question: 'Do you offer couples massage in Cancun?',
    answer: 'Yes. Our Cancun studio has a private double treatment room for couples massage. Each guest can choose the massage technique and pressure that best suits their needs.',
  },
  {
    question: 'What type of massage should I choose?',
    answer: 'You do not need to decide before your appointment. Our certified therapists can personalize your session using relaxation, therapeutic, deep tissue, sports or muscle recovery, lymphatic, Shiatsu and cupping techniques. Thai Massage and Hot Stones require a 90-minute booking; prenatal massage is available as a 60-minute session. Request Thai, cupping, Hot Stones or a male therapist when booking so we can confirm availability.',
  },
  {
    question: 'Is ABBA Massage near the Cancun Hotel Zone?',
    answer: 'Yes. ABBA Massage is located in downtown Cancun and is easily accessible from the Hotel Zone. In the app, search for "Abba Massage Studio & Wellness Center".',
  },
  {
    question: 'Can I book a same-day massage in Cancun?',
    answer: 'Yes. Same-day massage appointments are available when our schedule allows. Contact us on WhatsApp to check the latest availability.',
  },
  {
    question: 'Why choose ABBA Massage instead of a hotel or resort spa in Cancun?',
    answer: 'ABBA Massage offers a personalized, unrushed massage experience with experienced therapists and transparent pricing: MXN $960 for 60 minutes and MXN $1,320 for 90 minutes. Even after paying for an Uber from the Hotel Zone, many guests find they receive an exceptional experience for a fraction of typical resort-spa prices. One recent Google reviewer described her massage with us as "elite-tier" and quite possibly one of the best massages she had ever received.',
  },
],
    },
    founder: {
      label: 'Founder',
      title: 'Meet the Founder',
      name: 'Mariana, Founder.',
      description: 'Former Luxury Resort Spa Executive with 25+ years in hospitality and 15+ years leading luxury spa operations.',
      description2: 'ABBA Massage combines resort-level standards with personalized, genuine care.',
      cta: 'Meet Mariana',
    },
  },
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

// Icon mapping for Why Guests Return section
const getWhyReturnIcon = (iconName) => {
  const icons = {
    star: Star,
    award: Award,
    clock: Clock,
    leaf: Leaf,
    sound: Volume2,
    dollar: DollarSign,
    car: Car,
    globe: Globe,
    home: Home,
  };
  return icons[iconName] || Star;
};

export const StudioServices = () => {
  const { language } = useLanguage();
  const t = content[language];
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Add route-specific metadata for search engines and social sharing.
  useEffect(() => {
    const title = 'Massage Studio in Cancun | ABBA Massage';
    const description = 'Book a personalized massage at our private studio in Cancun. Open daily from 9 AM to 8 PM by appointment. 60 and 90-minute sessions available.';
    const canonicalUrl = 'https://abbamassage.com/studio-services';

    const previousTitle = document.title;
    document.title = title;

    const setMeta = (selector, attributes) => {
      let element = document.head.querySelector(selector);
      const created = !element;
      if (created) {
        element = document.createElement(attributes.tagName || 'meta');
        document.head.appendChild(element);
      }
      const previousValues = {};
      Object.entries(attributes).forEach(([name, value]) => {
        if (name !== 'tagName') {
          previousValues[name] = element.getAttribute(name);
          element.setAttribute(name, value);
        }
      });
      return () => {
        if (created) {
          element.remove();
        } else {
          Object.entries(previousValues).forEach(([name, value]) => {
            if (value === null) element.removeAttribute(name);
            else element.setAttribute(name, value);
          });
        }
      };
    };

    const restore = [
      setMeta('meta[name="description"]', { name: 'description', content: description }),
      setMeta('link[rel="canonical"]', { tagName: 'link', rel: 'canonical', href: canonicalUrl }),
      setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' }),
      setMeta('meta[property="og:title"]', { property: 'og:title', content: title }),
      setMeta('meta[property="og:description"]', { property: 'og:description', content: description }),
      setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl }),
      setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' }),
      setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title }),
      setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description }),
    ];

    return () => {
      document.title = previousTitle;
      restore.reverse().forEach((restoreElement) => restoreElement());
    };
  }, []);

  // Add route-specific structured data for search engines.
  useEffect(() => {
    const staticSchema = document.getElementById('studio-services-static-schema');
    if (staticSchema) return undefined;

    const schemaScript = document.createElement('script');
    schemaScript.id = 'studio-services-schema';
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify(STUDIO_SCHEMA);
    document.head.appendChild(schemaScript);

    return () => {
      schemaScript.remove();
    };
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % t.testimonials.items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [t.testimonials.items.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % t.testimonials.items.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + t.testimonials.items.length) % t.testimonials.items.length);
  };

  return (
    <div data-testid="studio-page" className="bg-white">
      
      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center pt-20" data-testid="studio-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.nav variants={fadeInUp} aria-label="Breadcrumb" className="mb-4 text-sm text-[#5A5A5A] font-['Manrope']">
                <Link to="/" className="hover:text-[#A91B60] transition-colors">
                  {language === 'es' ? 'Inicio' : 'Home'}
                </Link>
                <span aria-hidden="true" className="mx-2">/</span>
                <span>{language === 'es' ? 'Estudio de masajes en Cancún' : 'Massage studio in Cancun'}</span>
              </motion.nav>

              {/* Badge */}
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#A91B60]/10 rounded-full px-4 py-2 mb-6">
                <MapPin className="w-4 h-4 text-[#A91B60]" />
                <span className="text-sm text-[#A91B60] font-['Manrope'] font-medium">{t.hero.badge}</span>
              </motion.div>

              {/* Title */}
              <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-[#1C1C1C] mb-4 leading-tight">
                {t.hero.title}
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-base text-[#5A5A5A] font-['Manrope'] leading-relaxed mb-6 max-w-xl">
                {t.hero.summary}
              </motion.p>

              {/* Subtitle */}
              <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] font-['Manrope'] mb-2">
                {t.hero.subtitle}
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] font-['Manrope'] mb-8">
                {t.hero.subtitleLine2}
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-8">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    {t.hero.cta}
                  </Button>
                </a>
                <a href="#pricing">
                  <Button variant="outline" className="border-[#1C1C1C] text-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    {t.hero.ctaSecondary}
                  </Button>
                </a>
              </motion.div>

              {/* Trust indicators */}
              <motion.div variants={fadeInUp} className="flex items-center gap-6 text-sm text-[#5A5A5A] font-['Manrope']">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                  <span className="ml-2">5.0 Google</span>
                </div>
                <span>Top Rated TripAdvisor</span>
                <span>15+ {language === 'es' ? 'Años' : 'Yrs'}</span>
              </motion.div>
            </motion.div>

            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <img
                src={IMAGES.hero}
                alt={language === 'es' ? 'Masaje terapéutico en el estudio de ABBA Massage en Cancún' : 'Therapeutic massage at ABBA Massage Studio in Cancun'}
                className="w-full rounded-2xl shadow-xl object-cover aspect-[4/5]"
              />
              {/* WhatsApp badge */}
              <a 
                href={WHATSAPP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 flex items-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-2 shadow-lg hover:bg-[#128C7E] transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="font-['Manrope'] font-medium text-sm">WhatsApp</span>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY GUESTS RETURN SECTION */}
      <section className="py-16 bg-[#FAFAFA]" data-testid="why-return">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-10"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {t.whyReturn.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">
              {t.whyReturn.title}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-x-8 gap-y-4"
          >
            {t.whyReturn.items.map((item, index) => {
              const IconComponent = getWhyReturnIcon(item.icon);
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-center gap-4 py-3"
                >
                  <div className="w-10 h-10 rounded-full bg-[#A91B60]/10 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-[#A91B60]" strokeWidth={1.5} />
                  </div>
                  <span className="text-[#5A5A5A] font-['Manrope'] font-medium">{item.text}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="pricing" className="py-20" data-testid="pricing">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {t.pricing.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-2">
              {t.pricing.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] mb-12">
              {t.pricing.subtitle}
            </motion.p>

            <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto mb-6">
              {t.pricing.items.map((item, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
                >
                  <p className="text-sm text-[#5A5A5A] font-['Manrope'] uppercase tracking-wider mb-4">
                    {item.duration}
                  </p>
                  <p className="text-4xl font-bold text-[#1C1C1C] font-['Manrope']">
                    MXN {item.mxn}
                  </p>
                  <p className="text-lg text-[#A91B60] font-['Manrope'] mt-1">
                    USD {item.usd}
                  </p>
                </div>
              ))}
            </motion.div>

            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] mb-10">
              {language === 'es' 
                ? 'Elige la técnica que necesitas, sin cargos adicionales.'
                : 'Choose the technique you need, with no additional fees.'}
            </motion.p>

            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto mb-10">
              <h3 className="font-['Playfair_Display'] text-2xl text-[#1C1C1C] mb-5">
                {t.pricing.techniquesTitle}
              </h3>
              <div className="flex flex-wrap justify-center gap-3 mb-5">
                {t.pricing.techniques.map((technique, index) => (
                  <span
                    key={index}
                    className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm text-[#1C1C1C] font-['Manrope']"
                  >
                    {technique}
                  </span>
                ))}
              </div>
              <p className="text-sm text-[#5A5A5A] font-['Manrope'] leading-relaxed">
                {t.pricing.techniqueNote}
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {language === 'es' ? 'Reservar Ahora' : 'Book Now'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
              <a href={PRICE_LIST[language]} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#1C1C1C] text-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  <Download className="mr-2 w-4 h-4" />
                  {t.pricing.cta}
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PRIVATE STUDIO SECTION */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="studio-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={IMAGES.studio}
                alt={language === 'es' ? 'Interior del estudio privado de ABBA Massage en Cancún' : 'Interior of the private ABBA Massage Studio in Cancun'}
                className="w-full rounded-2xl shadow-xl object-cover aspect-[4/3]"
              />
            </motion.div>

            {/* Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
                {t.studio.label}
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-6 leading-tight">
                {t.studio.title}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] mb-4 leading-relaxed">
                {t.studio.description}
              </motion.p>
              <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] mb-8 leading-relaxed">
                {t.studio.description2}
              </motion.p>

              <motion.ul variants={fadeInUp} className="space-y-3">
                {t.studio.bullets.map((bullet, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#5A5A5A] font-['Manrope']">
                    <Check className="w-5 h-5 text-[#A91B60]" />
                    {bullet}
                  </li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THERAPISTS SECTION */}
      <section className="py-20" data-testid="therapists-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="order-2 lg:order-1"
            >
              <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
                {t.therapists.label}
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-6">
                {t.therapists.title}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] mb-8 leading-relaxed">
                {t.therapists.description}
              </motion.p>

              {/* Stats */}
              <motion.div variants={fadeInUp} className="flex gap-8">
                {t.therapists.stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="text-3xl font-bold text-[#1C1C1C] font-['Manrope']">{stat.value}</p>
                    <p className="text-sm text-[#5A5A5A] font-['Manrope'] uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <img
                src={IMAGES.therapists}
                alt={language === 'es' ? 'Equipo de terapeutas profesionales de ABBA Massage en Cancún' : 'Professional therapists at ABBA Massage in Cancun'}
                className="w-full rounded-2xl shadow-xl object-cover aspect-[4/3]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS CAROUSEL */}
      <section className="py-20 bg-[#1C1C1C]" data-testid="testimonials">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {t.testimonials.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-2">
              {t.testimonials.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 font-['Manrope'] mb-12">
              {t.testimonials.subtitle}
            </motion.p>

            {/* Carousel */}
            <motion.div variants={fadeInUp} className="relative">
              <div className="bg-white rounded-2xl p-8 md:p-12 min-h-[200px] flex flex-col items-center justify-center">
                <Quote className="w-10 h-10 text-[#A91B60]/20 mb-6" />
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTestimonial}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="text-center"
                  >
                    <p className="text-xl md:text-2xl text-[#1C1C1C] font-['Playfair_Display'] italic mb-6 leading-relaxed">
                      &ldquo;{t.testimonials.items[currentTestimonial].text}&rdquo;
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm text-[#5A5A5A] font-['Manrope'] uppercase tracking-wider">
                        Verified Guest
                      </span>
                      <span className="text-[#A91B60]">·</span>
                      <span className="text-sm text-[#A91B60] font-['Manrope'] font-semibold">
                        {t.testimonials.items[currentTestimonial].source}
                      </span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={prevTestimonial}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                
                <div className="flex gap-2">
                  {t.testimonials.items.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentTestimonial ? 'bg-[#A91B60]' : 'bg-white/30'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FOUNDER SECTION - Links to About page */}
      <section className="py-20" data-testid="founder">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {t.founder.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-6">
              {t.founder.title}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-[#1C1C1C] font-['Manrope'] font-semibold mb-4">
              {t.founder.name}
            </motion.p>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] mb-2 max-w-2xl mx-auto">
              {t.founder.description}
            </motion.p>
            <motion.p variants={fadeInUp} className="text-[#5A5A5A] font-['Manrope'] mb-8 max-w-2xl mx-auto">
              {t.founder.description2}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/about">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {t.founder.cta}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-20 bg-white" data-testid="studio-faq">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {t.faq.label}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">
              {t.faq.title}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {t.faq.items.map((item, index) => (
              <motion.details
                key={index}
                variants={fadeInUp}
                className="group border border-gray-200 rounded-2xl p-6 bg-[#FAFAFA]"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-['Manrope'] font-semibold text-[#1C1C1C]">
                  {item.question}
                  <ChevronDown className="w-5 h-5 text-[#A91B60] flex-shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-4 text-[#5A5A5A] font-['Manrope'] leading-relaxed">
                  {item.answer}
                </p>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>

      {/* LOCATION & SOCIAL SECTION */}
      <section className="py-16 bg-[#FAFAFA]" data-testid="location-social">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h3 variants={fadeInUp} className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1C1C1C] mb-6">
              {language === 'es' ? 'Encuéntranos' : 'Find Us'}
            </motion.h3>
            
            {/* Social & Location Icons */}
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4 mb-6">
              {/* Instagram */}
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:shadow-md hover:border-[#A91B60] transition-all group"
                title="Instagram"
              >
                <Instagram className="w-5 h-5 text-[#5A5A5A] group-hover:text-[#A91B60]" />
              </a>
              
              {/* Facebook */}
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:shadow-md hover:border-[#A91B60] transition-all group"
                title="Facebook"
              >
                <Facebook className="w-5 h-5 text-[#5A5A5A] group-hover:text-[#A91B60]" />
              </a>
              
              {/* Google Maps */}
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:shadow-md hover:border-[#A91B60] transition-all group"
                title="Google Maps"
              >
                <MapPin className="w-5 h-5 text-[#5A5A5A] group-hover:text-[#A91B60]" />
              </a>
              
              {/* TripAdvisor */}
              <a
                href={TRIPADVISOR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:shadow-md hover:border-[#A91B60] transition-all group"
                title="TripAdvisor"
              >
                <svg className="w-5 h-5 text-[#5A5A5A] group-hover:text-[#00AF87]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                </svg>
              </a>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="mb-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-[#5A5A5A] font-['Manrope']"
            >
              <span className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#A91B60]" />
                {language === 'es' ? 'Todos los días: 9:00 a. m.–8:00 p. m.' : 'Open daily: 9:00 AM–8:00 PM'}
              </span>
              <span className="inline-flex items-center gap-2 font-semibold text-[#1C1C1C]">
                <Calendar className="w-4 h-4 text-[#A91B60]" />
                {language === 'es' ? 'Únicamente con cita previa' : 'By appointment only'}
              </span>
            </motion.div>

            {/* Address with Google Maps link */}
            <motion.a
              variants={fadeInUp}
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#5A5A5A] hover:text-[#A91B60] font-['Manrope'] transition-colors"
            >
              <MapPin className="w-4 h-4" />
              <span>Calle Playa Brava 26, Mz. 1, Lote 53, 77508 Cancún, Q.R.</span>
            </motion.a>

            <motion.div variants={fadeInUp} className="mt-6">
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#A91B60] text-[#A91B60] hover:bg-[#A91B60] hover:text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  <MapPin className="mr-2 w-5 h-5" />
                  {language === 'es' ? 'Cómo llegar en Google Maps' : 'Get Directions on Google Maps'}
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default StudioServices;
